/**
 * User 应用主题状态管理
 * 使用 Zustand 管理主题配置和切换
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ThemeConfig } from '@ifa/ui';
import { applyTokensToCSS, toAntdTheme } from '@ifa/ui';
import userTheme from './default';
import type { ThemeConfig as AntdThemeConfig } from 'antd';

/**
 * 主题状态接口
 */
interface ThemeState {
  /**
   * 当前主题配置
   */
  theme: ThemeConfig;

  /**
   * Ant Design 主题配置（自动生成）
   */
  antdTheme: AntdThemeConfig;

  /**
   * 设置主题
   * @param theme 新的主题配置
   */
  setTheme: (theme: ThemeConfig) => void;

  /**
   * 重置为默认主题
   */
  resetTheme: () => void;

  /**
   * 应用主题到 DOM
   */
  applyTheme: () => void;
}

/**
 * User 应用主题 Store
 */
export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      // 初始状态
      theme: userTheme,
      antdTheme: toAntdTheme(userTheme.tokens),

      // 设置主题
      setTheme: (theme: ThemeConfig) => {
        // 应用到 CSS 变量
        applyTokensToCSS(theme.tokens);

        // 生成 Ant Design 主题
        const antdTheme = toAntdTheme(theme.tokens);

        set({ theme, antdTheme });
      },

      // 重置为默认主题
      resetTheme: () => {
        get().setTheme(userTheme);
      },

      // 应用主题到 DOM（用于初始化）
      applyTheme: () => {
        const { theme } = get();
        applyTokensToCSS(theme.tokens);
      },
    }),
    {
      name: 'user-theme-storage',
      // 只持久化 theme 配置，antdTheme 自动生成
      partialize: (state) => ({ theme: state.theme }),
      // 从 localStorage 恢复后重新生成 antdTheme
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.antdTheme = toAntdTheme(state.theme.tokens);
          state.applyTheme();
        }
      },
    }
  )
);

/**
 * 初始化主题（在应用启动时调用）
 */
export function initializeTheme(): void {
  const { applyTheme } = useThemeStore.getState();
  applyTheme();
}

/**
 * 获取当前 Ant Design 主题配置（用于 ConfigProvider）
 */
export function getAntdTheme(): AntdThemeConfig {
  return useThemeStore.getState().antdTheme;
}
